import {inject, Injectable} from '@angular/core';
import {HttpContextToken, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest} from '@angular/common/http';
import {finalize, Observable, shareReplay} from 'rxjs';
import {RequestDedupeMonitor} from './request-dedupe-monitor.service';

/**
 * Opt-out marker: set this on a request's {@link HttpContext} to bypass duplicate-request deduplication, e.g.
 *
 * ```ts
 * import {HttpContext} from '@angular/common/http';
 * import {ALLOW_DUPLICATE_REQUESTS} from '../x-interceptors/duplicate-request.interceptor';
 *
 * this.http.post('/api/rows', body, {context: new HttpContext().set(ALLOW_DUPLICATE_REQUESTS, true)});
 * ```
 *
 * Use it only for the rare endpoint where firing the *same* payload twice in parallel is legitimate (e.g. a
 * rapid-fire "add identical row" action). Everywhere else the default (dedupe on) is what you want.
 */
export const ALLOW_DUPLICATE_REQUESTS = new HttpContextToken<boolean>(() => false);

/**
 * Stable dedupe-key override: set this on a request's {@link HttpContext} to key deduplication on your own
 * identity string instead of the serialized body, e.g.
 *
 * ```ts
 * import {HttpContext} from '@angular/common/http';
 * import {DEDUPE_KEY} from '../x-interceptors/duplicate-request.interceptor';
 *
 * this.http.post('/api/orders', body, {context: new HttpContext().set(DEDUPE_KEY, `order:${body.orderId}`)});
 * ```
 *
 * The default (body-keyed) dedupe collapses two clicks only when their bodies are byte-identical. That breaks
 * in two ways: when a field is (re)generated per send — a `Date.now()` timestamp, a nonce, a correlation id —
 * because the two bodies then differ; and when the body is a type this interceptor cannot compare at all
 * (`FormData`/`Blob`/`ArrayBuffer`/`URLSearchParams`), which is otherwise passed through **un-deduped**.
 * Supply a `DEDUPE_KEY` derived from the request's *real* identity (an entity id, an operation name) and
 * identical concurrent requests collapse regardless of the body — **including an upload**, which is the only
 * way a multipart request gets double-submit protection at all. This is the client-side idempotency-key hook:
 * it is opt-in and precise, so it never risks collapsing two genuinely different requests the way blanket
 * body-normalisation would. `null`/empty (the default) ⇒ key on the body.
 *
 * The key must be **stable across the two clicks of one attempt**. A value minted at send time (`Date.now()`,
 * a fresh uuid) makes the two attempts differ and defeats the whole mechanism — derive it from what identifies
 * the attempt instead.
 */
export const DEDUPE_KEY = new HttpContextToken<string | null>(() => null);

/** Mutating HTTP methods — the only ones a double-click can turn into a colliding double-insert. */
const MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/** Per-key bookkeeping for one in-flight (deduped) request. */
interface InFlightEntry {
  /** The shared, replayed observable every caller (originator + duplicates) subscribes to. */
  shared$: Observable<HttpEvent<unknown>>;
  /** How many *duplicate* callers have attached — reported to the monitor on settle so it can zero them out. */
  waiting: number;
}

/**
 * Central deduplication of identical, concurrent mutating HTTP requests (Bug #143).
 *
 * A double-click — or any accidental repeat trigger — fires two byte-for-byte-identical mutating requests
 * before the first has returned. Both reach the backend and both insert, producing the collision this bug is
 * about. This interceptor collapses that: while an identical mutating request is in flight, later identical
 * requests **never hit the backend** — they subscribe to the *same* shared response and receive the exact
 * same events (via {@link shareReplay}) the originator does. The duplicate is not re-issued after the first
 * completes; it shares the first's outcome, success or error.
 *
 * **Ordering matters.** This must be registered FIRST in `interceptors.config.ts` so it is the *outermost*
 * interceptor. A deduped caller returns the shared observable and therefore never re-runs the inner chain
 * (Auth → Error → Date). That is deliberate: the real request runs the inner chain exactly once, so any
 * side-effect down there — most importantly `ErrorInterceptor`'s 401→logout — fires exactly once per real
 * request, not once per double-click. See `docs/articles/frontend/http-request-deduplication.md`.
 *
 * **What is NOT deduped** (passes straight through):
 * - Non-mutating methods (GET/HEAD/OPTIONS) — they don't insert, so a double-fetch is harmless.
 * - Requests whose {@link HttpContext} carries {@link ALLOW_DUPLICATE_REQUESTS} — the explicit opt-out.
 * - Requests whose body isn't reliably comparable (`FormData`/`Blob`/`ArrayBuffer`/`URLSearchParams`) **and**
 *   that carry no {@link DEDUPE_KEY} — we can't cheaply key on their content, so we never risk collapsing two
 *   genuinely different uploads. An upload that *does* declare a `DEDUPE_KEY` is deduped on that key: that is
 *   the only double-submit protection a multipart request can have, and any endpoint where a double-tap would
 *   insert twice (e.g. a form submission with attachments) must supply one.
 */
@Injectable()
export class DuplicateRequestInterceptor implements HttpInterceptor {
  private readonly monitor = inject(RequestDedupeMonitor);

  /** Live map of dedupe key → the single shared observable every identical caller rides. */
  private readonly inFlight = new Map<string, InFlightEntry>();

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!this.shouldDedupe(request)) {
      return next.handle(request);
    }

    const key = this.buildKey(request);
    const existing = this.inFlight.get(key);
    if (existing) {
      // A duplicate arrived while the real request is still in flight: hand back the SAME shared observable so
      // it replays the originator's response. It never reaches `next`, so the backend sees exactly one call.
      existing.waiting++;
      this.monitor.registerDuplicate(key);
      return existing.shared$;
    }

    // First of its kind: run the real request once, share+replay it to any duplicates, and clean up on settle
    // (success OR error OR all-unsubscribed) so a later *sequential* identical request goes out normally.
    const entry: InFlightEntry = {waiting: 0, shared$: undefined!};
    entry.shared$ = next.handle(request).pipe(
      finalize(() => {
        // Guard against a stale delete: only remove (and settle) if THIS entry still owns the key. A fresh
        // sequential request could have re-claimed the key between the last unsubscribe and this teardown.
        if (this.inFlight.get(key) === entry) {
          this.inFlight.delete(key);
          this.monitor.settle(key, entry.waiting);
        }
      }),
      // refCount:true → if every subscriber unsubscribes the real request cancels (normal HttpClient
      // semantics); a single unsubscribe leaves the others running. bufferSize:1 → a late duplicate that
      // subscribes after the response emitted but before finalize cleaned up still replays that response.
      shareReplay({bufferSize: 1, refCount: true})
    );

    this.inFlight.set(key, entry);
    this.monitor.registerInFlight(key);
    return entry.shared$;
  }

  /**
   * Whether this request is eligible for dedupe: a mutating method, not opted out, and either carrying an
   * explicit {@link DEDUPE_KEY} or a comparable body.
   *
   * The explicit key is checked **before** the body: it *is* the caller's declared identity, so the body's
   * comparability stops being relevant. Without this precedence an upload could never be deduped — the
   * `FormData` bail below would refuse it before {@link buildKey} ever looked at the key — which is exactly how
   * a multipart submit silently lost its double-click protection.
   */
  private shouldDedupe(request: HttpRequest<unknown>): boolean {
    if (request.context.get(ALLOW_DUPLICATE_REQUESTS)) {
      return false;
    }
    if (!MUTATING_METHODS.has(request.method.toUpperCase())) {
      return false;
    }
    if (request.context.get(DEDUPE_KEY)) {
      return true;
    }
    return this.hasComparableBody(request.body);
  }

  /**
   * A body is comparable only if it serializes to a stable string. `FormData`/`Blob`/`ArrayBuffer`/
   * `URLSearchParams` don't (or would be prohibitively expensive to), so we treat those as unique and let
   * them through untouched rather than risk collapsing two genuinely different payloads.
   */
  private hasComparableBody(body: unknown): boolean {
    if (body === null || body === undefined) {
      return true; // an empty-body mutation (e.g. a bare POST toggle) is perfectly comparable
    }
    return !(
      body instanceof FormData ||
      body instanceof Blob ||
      body instanceof ArrayBuffer ||
      ArrayBuffer.isView(body) ||
      body instanceof URLSearchParams
    );
  }

  /**
   * Dedupe key = method + full URL (incl. query params) + body-identity. Two requests share an in-flight
   * response only when all three match exactly. The body-identity is the caller's {@link DEDUPE_KEY} override
   * when set (so requests with per-send volatile fields still collapse), otherwise Angular's own
   * {@link HttpRequest.serializeBody} — the bytes that would actually go on the wire.
   */
  private buildKey(request: HttpRequest<unknown>): string {
    const prefix = `${request.method.toUpperCase()} ${request.urlWithParams} `;

    const override = request.context.get(DEDUPE_KEY);
    if (override) {
      // Explicit identity: key on the caller's stable string, ignoring volatile body fields entirely.
      return `${prefix}${override}`;
    }

    const body = request.serializeBody();
    // serializeBody() returns ArrayBuffer/Blob/FormData verbatim for those body types, but hasComparableBody()
    // has already excluded them — so here it is always a string or null.
    return `${prefix}${typeof body === 'string' ? body : ''}`;
  }
}
